import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import {
  Icon,
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2DataGrid,
  V2EmptyState,
  V2ToastStack,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type { MovementListResponse } from '../../lib/types/inventoryMovements.js';
import { MovementForm } from './MovementForm.js';
import { MOVEMENT_STATUS_META, metaOf, sourceTypeLabel } from './inventoryDisplay.js';
import { useToasts } from './useInventoryUi.js';
import { useReceiveTransfer } from './useReceiveTransfer.js';
import './inventory.css';

type MovementRow = MovementListResponse['movements'][number];

/**
 * Shared by the receipt/issue/transfer list routes — same real endpoint
 * shape (`GET .../inventory/{receipts,issues,transfers}`), same real
 * create form (`MovementForm`, `POST` to the matching endpoint —
 * real, tested, and permission-gated per `movements.controller.ts`).
 *
 * The list itself only shows the fields the real API returns: no
 * counterparty/warehouse/line-count columns exist at the list level
 * (`MovementListItem` has no such fields — only a movement's own detail
 * endpoint has line data, and it isn't indexed by warehouse either), so
 * this never fabricates a "طرف‌حساب"/"انبار"/"اقلام" column.
 */
export function MovementListPage({
  endpoint,
  variant,
  title,
  emptyLabel,
  emptyDescription,
}: {
  readonly endpoint: string;
  readonly variant: 'receipt' | 'issue' | 'transfer';
  readonly title: string;
  readonly emptyLabel: string;
  readonly emptyDescription: string;
}) {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);
  const [query, setQuery] = useState('');
  const { toasts, push, dismiss } = useToasts();
  const result = useApiGet<MovementListResponse>(
    slug ? `${base}/api/v1/inventory/${endpoint}?limit=200` : null,
    [slug, endpoint, refreshKey],
  );
  const receiveTransfer = useReceiveTransfer(base, () => {
    setRefreshKey((k) => k + 1);
    push('وصول انتقال ثبت شد.');
  });

  if (!slug) return null;

  const q = query.trim().toLowerCase();
  const rows =
    result.status === 'ready'
      ? result.data.movements.filter(
          (m) =>
            !q ||
            m.movementNumber.toLowerCase().includes(q) ||
            (m.sourceReference && m.sourceReference.toLowerCase().includes(q)) ||
            (m.partyName && m.partyName.toLowerCase().includes(q)) ||
            (m.partyCode && m.partyCode.toLowerCase().includes(q)),
        )
      : [];

  return (
    <div className="inv-page">
      <MovementForm variant={variant} onCreated={() => setRefreshKey((k) => k + 1)} />

      {receiveTransfer.error ? (
        <V2Alert tone="danger" onClose={receiveTransfer.clearError}>
          {receiveTransfer.error}
        </V2Alert>
      ) : null}

      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <StatePanel
          variant={result.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={result.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}

      {result.status === 'ready' ? (
        <V2DataGrid
          icon="inventory"
          title={title}
          subtitle={`${(query ? rows.length : (result.data.total ?? result.data.movements.length)).toLocaleString('fa-IR')} سند`}
          search={{ value: query, onChange: setQuery, placeholder: 'شمارهٔ سند یا مرجع' }}
          columns={[
            {
              key: 'number',
              header: 'شمارهٔ سند',
              width: 150,
              render: (row: MovementRow) => (
                <Link
                  to={`${base}/inventory/movements/${row.movementId}`}
                  className="ltr sk-num inv-code inv-link"
                >
                  {row.movementNumber}
                </Link>
              ),
            },
            {
              key: 'date',
              header: 'تاریخ',
              width: 120,
              render: (row: MovementRow) => (
                <span className="sk-num">{displayJalaliDate(row.businessDate)}</span>
              ),
            },
            {
              key: 'party',
              header:
                variant === 'receipt'
                  ? 'تأمین‌کننده'
                  : variant === 'issue'
                    ? 'مشتری / تحویل‌گیرنده'
                    : 'طرف‌حساب',
              width: 180,
              render: (row: MovementRow) =>
                row.partyName ? (
                  <span className="inv-cell">
                    <span className="inv-cell__main">{row.partyName}</span>
                    <span className="inv-cell__sub ltr">{row.partyCode}</span>
                  </span>
                ) : (
                  <span className="ltr">{row.partyCode ?? '—'}</span>
                ),
            },
            {
              key: 'sourceRef',
              header: 'شماره سفارش / مرجع',
              width: 150,
              render: (row: MovementRow) =>
                row.sourceReference ? (
                  <span className="ltr sk-num">{row.sourceReference}</span>
                ) : (
                  <span>—</span>
                ),
            },
            {
              key: 'source',
              header: 'منبع',
              width: 120,
              render: (row: MovementRow) => (
                <span className="sk-tag">{sourceTypeLabel(row.sourceType)}</span>
              ),
            },
            {
              key: 'status',
              header: 'وضعیت',
              width: 120,
              render: (row: MovementRow) => (
                <V2Badge tone={metaOf(MOVEMENT_STATUS_META, row.status).tone}>
                  {metaOf(MOVEMENT_STATUS_META, row.status).label}
                </V2Badge>
              ),
            },
            {
              key: 'actions',
              header: 'عملیات',
              width: variant === 'transfer' ? 180 : 110,
              render: (row: MovementRow) => (
                <span className="inv-actions">
                  {row.status === 'in_transit' ? (
                    <V2Button
                      size="sm"
                      variant="primary"
                      disabled={receiveTransfer.busyId === row.movementId}
                      onClick={() => void receiveTransfer.receive(row.movementId)}
                      icon="check"
                      title="تأیید وصول در مقصد"
                    >
                      {receiveTransfer.busyId === row.movementId ? 'در حال وصول…' : 'وصول'}
                    </V2Button>
                  ) : null}
                  <Link
                    to={`${base}/inventory/movements/${row.movementId}/print`}
                    target="_blank"
                    className="sk-btn sk-btn--ghost sk-btn--sm inv-btn-link"
                    title="چاپ برگه رسمی انبار"
                  >
                    <Icon name="invoice" size="sm" />
                    <span>چاپ</span>
                  </Link>
                </span>
              ),
            },
          ]}
          rows={rows}
          rowKey={(row) => row.movementId}
          emptyState={
            <V2EmptyState
              icon="inventory"
              title={query ? 'سندی با این شماره پیدا نشد' : emptyLabel}
              description={query ? undefined : emptyDescription}
              compact
            />
          }
        />
      ) : null}

      <V2ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
